import { FirestoreDatabase, storageRef } from '../index'
import {
  AttendeeRoles,
  Collection,
  organizationStatus,
  orgRepresentativeStatus
} from 'constants/index'
import { toast } from 'utils/notify'
import { nanoid } from 'nanoid'
import { chunk } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'
import { signOut } from 'store/auth'
import moment from 'moment'
import sponsorSlice from './sponsor'

const DEFAULT_STATUS_FILTER = 'all'
const DEFAULT_TYPE_FILTER = 'all'

export const initialState = {
  organizations: [],
  organization: {},
  statusFilter: DEFAULT_STATUS_FILTER,
  typeFilter: DEFAULT_TYPE_FILTER,
  representatives: [],
  currentPage: 0,
  pageSize: 10
}

const organizationSlice = createSlice({
  name: 'organization',
  initialState: initialState,
  reducers: {
    setOrganizations: (state, action) => {
      state.organizations = action.payload
    },
    setOrganization: (state, action) => {
      state.organization = action.payload
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload
    },
    setTypeFilter: (state, action) => {
      state.typeFilter = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setOrganizationRepresentatives: (state, action) => {
      state.representatives = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.statusFilter = DEFAULT_STATUS_FILTER
      state.typeFilter = DEFAULT_TYPE_FILTER
      state.organizations = []
      state.organization = {}
      state.representatives = []
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const resetOrganization = () => (dispatch) => {
  dispatch(organizationSlice.actions.setOrganization({}))
}

export const fetchOrganizationById = (organizationId) => async (dispatch) => {
  try {
    const doc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .doc(organizationId)
      .get()
    dispatch(
      organizationSlice.actions.setOrganization({
        id: doc.id,
        ...doc.data()
      })
    )
    return doc.id
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const addOrganization = async (payload, conferenceId) => {
  try {
    const { organization } = payload
    const snapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('organization', '==', organization)
      .where('conferenceId', '==', conferenceId)
      .get()

    if (snapshot.empty) {
      const doc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS).add({
        ...payload,
        status: organizationStatus.INCOMPLETE,
        conferenceId: conferenceId,
        token: nanoid(),
        createdAt: moment().utc().valueOf()
      })

      return doc.id
    } else {
      toast.error('An organization with this name already exists.')
    }
  } catch (err) {
    console.error(err)
    toast.error(err.message)
    return null
  }
}

export const updateOrganizationById = async (organizationId, payload, conferenceId) => {
  try {
    const { organization } = payload
    const orgDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .doc(organizationId)
      .get()
    const currentOrg = orgDoc.exists ? { id: orgDoc.id, ...orgDoc.data() } : null

    if (!currentOrg) {
      toast.error('Organization not found.')
      return null
    }

    const snapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('organization', '==', organization)
      .where('conferenceId', '==', conferenceId)
      .get()

    const matchingSnapshot = snapshot.docs.filter((doc) => doc.id !== organizationId)

    if (!matchingSnapshot.length) {
      await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .doc(organizationId)
        .update(payload)
      return organizationId
    } else {
      toast.warning('An organization with this name already exists.')
    }
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const approveOrganizationById = async (organizationId) => {
  try {
    const orgDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .doc(organizationId)
      .get()

    if (!orgDoc.exists) {
      toast.error('Organization not found.')
      return null
    }

    await FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc(organizationId).update({
      status: organizationStatus.APPROVED,
      approvedAt: moment().utc().valueOf()
    })

    return organizationId
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const getSponsorshipLevels = async (conferenceId) => {
  const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
    .where('conferenceId', '==', conferenceId)
    .orderBy('sponsorshipTier', 'desc')
    .get()

  const sponsorshipLevels = []
  snapshot.forEach((doc) => {
    sponsorshipLevels.push({
      id: doc.id,
      ...doc.data()
    })
  })

  return sponsorshipLevels
}

export const fetchConferenceOrganizations = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const organizations = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))

    return organizations
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const getOrganizations = (conferenceId, callback) => {
  try {
    return FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const organizations = []
        documents.forEach((doc) => {
          organizations.push({
            id: doc.id,
            ...doc.data()
          })
        })
        callback(organizations)
      })
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const deleteOrganizationById = async (organizationId, conferenceId) => {
  try {
    const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .where('sponsors', 'array-contains', organizationId)
      .get()

    const subEventsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('conferenceId', '==', conferenceId)
      .where('sponsors', 'array-contains', organizationId)
      .get()

    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .where('sponsors', 'array-contains', organizationId)
      .get()

    // Check if this organization can be deleted based on references in other collections
    if (!eventsSnapshot.empty || !subEventsSnapshot.empty || !talksSnapshot.empty) {
      toast.error(
        'This organization cannot be deleted because it is referenced in other event(s), sub event(s) or talk(s). To delete it, please remove it from all event(s) sub event(s) and talk(s) first.'
      )
      return false
    }

    // Get representatives associated with this organization
    const representativesSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('organizationId', '==', organizationId)
      .where('role', '==', AttendeeRoles.REPRESENTATIVE)
      .get()

    // If there are representatives, delete them in batches before proceeding
    if (!representativesSnapshot.empty) {
      // Get all representative document references
      const representativeRefs = representativesSnapshot.docs.map((doc) => doc.ref)

      // Process in chunks of 500 (Firestore batch limit)
      const batchSize = 500
      const batches = chunk(representativeRefs, batchSize)

      // Process each batch
      for (const batchOfReps of batches) {
        const batch = FirestoreDatabase.batch()

        // Add delete operations to batch
        batchOfReps.forEach((repRef) => {
          batch.delete(repRef)
        })

        // Commit the batch
        await batch.commit()
      }
    }

    // Now delete the organization itself
    const snapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .doc(organizationId)
      .get()
    const e = snapshot.data()
    try {
      await Promise.all([
        e?.adImageName &&
          storageRef.child(`${conferenceId}/${Collection.ORGANIZATIONS}/${e.adImageName}`).delete(),
        e?.logoImageName &&
          storageRef
            .child(`${conferenceId}/${Collection.ORGANIZATIONS}/${e.logoImageName}`)
            .delete(),
        e?.brochureImageName &&
          storageRef
            .child(`${conferenceId}/${Collection.ORGANIZATIONS}/${e.brochureImageName}`)
            .delete()
      ])
    } catch (err) {
      console.error(err)
    }
    await FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc(organizationId).delete()
    return true
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  }
}

export const addRepresentative = async (representativeData, conferenceId) => {
  try {
    const existingRepresentative = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('email', '==', representativeData.email)
      .where('conferenceId', '==', conferenceId)
      .get()
    if (!existingRepresentative.empty) {
      toast.warning('A representative with this email already exists.')
      return
    }

    // Add the representative as an attendee
    const doc = await FirestoreDatabase.collection(Collection.ATTENDEES).add(representativeData)
    if (representativeData.organizationId) {
      // dispatch(fetchOrganizationRepresentatives(representativeData.organizationId))
      if (representativeData.isPrimaryContact) {
        // Get current organization to check status
        const orgDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
          .doc(representativeData.organizationId)
          .get()
        const orgData = orgDoc.exists ? orgDoc.data() : null

        // Create update payload
        const updatePayload = {
          primaryContactId: doc.id
        }

        // If status was INCOMPLETE, update it to INVITE_NOT_SENT
        if (orgData && orgData.status === organizationStatus.INCOMPLETE) {
          updatePayload.status = organizationStatus.INVITE_NOT_SENT
        }

        // Update the organization with primary contact ID and potentially new status
        await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
          .doc(representativeData.organizationId)
          .update(updatePayload)
      }
    }
    toast.success('Representative added.')
    return doc.id
  } catch (error) {
    console.error('Error adding representative:', error)
    toast.error('Could not add representative.')
  }
}

export const updateRepresentative = async (representativeId, updatedData) => {
  try {
    const existingRepresentative = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('email', '==', updatedData?.email)
      .where('conferenceId', '==', updatedData?.conferenceId)
      .get()

    const existingReps = existingRepresentative.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    if (
      !existingRepresentative.empty &&
      existingReps.filter((rep) => rep.id !== representativeId).length > 0
    ) {
      toast.warning('A representative with this email already exists.')
      return
    }

    const currentRepresentative = existingReps.find((rep) => rep.id === representativeId)

    const orgDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .doc(updatedData.organizationId)
      .get()
    const currentOrg = orgDoc.exists ? { id: orgDoc.id, ...orgDoc.data() } : null

    if (
      currentOrg.primaryContactId &&
      (currentOrg.status === organizationStatus.PENDING_DATA_REVIEW ||
        currentOrg.status === organizationStatus.APPROVED) &&
      (updatedData.email !== currentRepresentative.email ||
        updatedData.isPrimaryContact !== currentRepresentative.isPrimaryContact)
    ) {
      toast.warning('Primary contact cannot be changed after a profile has been received.')
      return
    }

    // Get the current sponsor to check status if this is a primary contact change
    if (currentOrg && updatedData.isPrimaryContact) {
      const needsTokenRenewal =
        currentOrg.status === organizationStatus.AWAITING_RESPONSE &&
        currentRepresentative.email !== updatedData.email

      // If organization is in appropriate status, update the token
      if (needsTokenRenewal) {
        const newToken = nanoid()
        await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
          .doc(updatedData.organizationId)
          .update({ token: newToken })
      }
    }

    if (currentOrg.primaryContactId && !updatedData.isPrimaryContact) {
      await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .doc(updatedData.organizationId)
        .update({
          primaryContactId: null
        })
    }

    if (!currentOrg.primaryContactId && updatedData.isPrimaryContact) {
      // Create update payload
      const updatePayload = {
        primaryContactId: representativeId
      }

      // If status is INCOMPLETE, update it to INVITE_NOT_SENT
      if (currentOrg.status === organizationStatus.INCOMPLETE) {
        updatePayload.status = organizationStatus.INVITE_NOT_SENT
      }

      // Update the sponsor with primary contact ID and potentially new status
      await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .doc(updatedData.organizationId)
        .update(updatePayload)
    }

    // Update the attendee record
    await FirestoreDatabase.collection(Collection.ATTENDEES)
      .doc(representativeId)
      .update({
        ...updatedData,
        updatedAt: moment().utc().valueOf()
      })

    toast.success('Representative updated.')
    return representativeId
  } catch (error) {
    console.error('Error in updateRepresentative:', error)
    toast.error('Could not update representative.')
  }
}

export const deleteRepresentative = (representativeId) => async (dispatch) => {
  try {
    // Get the representative data to find the organizationId
    const attendeeDoc = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .doc(representativeId)
      .get()
    const attendeeData = attendeeDoc.data()
    const organizationId = attendeeData?.organizationId

    if (organizationId && attendeeData?.isPrimaryContact) {
      // Check if this representative is a primary contact for the sponsor
      const sponsorDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .doc(organizationId)
        .get()
      const sponsorData = sponsorDoc.data()

      // If this is the primary contact, update sponsor status to INCOMPLETE and clear primaryContactId
      if (sponsorData && sponsorData?.primaryContactId === representativeId) {
        await FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc(organizationId).update({
          status: organizationStatus.INCOMPLETE,
          primaryContactId: null
        })
      }
    }

    // Delete the attendee record
    await FirestoreDatabase.collection(Collection.ATTENDEES).doc(representativeId).delete()

    return representativeId
  } catch (error) {
    dispatch(sponsorSlice.actions.setError(error))
    throw error
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const bulkCreateRepresentatives = async (representativesDataArray, conferenceId) => {
  try {
    if (!representativesDataArray || !representativesDataArray.length) {
      return { added: 0, existing: 0 }
    }

    // 1. Get all existing representatives for this conference
    const existingRepsSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .get()

    const existingRepsMap = {}
    existingRepsSnapshot.docs.forEach((doc) => {
      const data = doc.data()
      existingRepsMap[data?.email.toLowerCase()] = { id: doc.id, ...data }
    })

    // 2. Compare and filter existing vs. new reps based on email
    const newReps = []
    const existingReps = []
    // Track representatives marked as primary contacts along with their organizations
    const primaryContactsMap = {}

    for (const rep of representativesDataArray) {
      if (!rep.email) continue

      const normalizedEmail = rep.email.toLowerCase()

      // Check if this email already exists in our map of existing representatives
      if (existingRepsMap[normalizedEmail]) {
        existingReps.push(rep)
      } else {
        newReps.push(rep)

        // If this rep is marked as primary contact and has an organizationId, track it
        if (rep.isPrimaryContact && rep.organizationId) {
          primaryContactsMap[normalizedEmail] = rep.organizationId
        }
      }
    }

    // 3. Batch create new representatives using chunks (Firestore limit: 500 writes per batch)
    const batchSize = 500
    const batchedRepsData = chunk(newReps, batchSize)

    // We'll track the created representatives to update their organizations later
    if (batchedRepsData.length > 0) {
      const batchedPromises = batchedRepsData.map((batchReps) => {
        const batch = FirestoreDatabase.batch()

        // Create a map of refs and their data for tracking created reps
        const refsMap = []

        batchReps.forEach((rep) => {
          rep.company = rep.organization
          rep.conferenceId = conferenceId
          rep.status = orgRepresentativeStatus.NOT_NOTIFIED
          rep.role = AttendeeRoles.REPRESENTATIVE
          rep.createdAt = moment().utc().valueOf()

          delete rep['organization']
          const newRepRef = FirestoreDatabase.collection(Collection.ATTENDEES).doc()
          batch.set(newRepRef, rep)

          // If this is a primary contact rep, track its reference and email
          if (rep.isPrimaryContact && rep.organizationId) {
            refsMap.push({
              ref: newRepRef,
              email: rep.email.toLowerCase(),
              organizationId: rep.organizationId
            })
          }
        })

        return { batch: batch.commit(), refs: refsMap }
      })

      // Execute all batch operations and collect refs
      await Promise.all(batchedPromises.map((item) => item.batch))

      // Get all refs to track for primary contact updates
      const allRefs = batchedPromises.flatMap((item) => item.refs)

      // 4. Update organizations for primary contacts
      if (allRefs.length > 0) {
        const orgBatch = FirestoreDatabase.batch()

        // For each primary contact, update their organization
        for (const refData of allRefs) {
          const organizationId = refData.organizationId

          // Get current organization status
          const orgDoc = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
            .doc(organizationId)
            .get()

          const orgData = orgDoc.exists ? orgDoc.data() : null

          if (orgData && !orgData?.primaryContactId) {
            // Create update payload
            const updatePayload = {
              primaryContactId: refData.ref.id
            }

            // If status was INCOMPLETE, update it to INVITE_NOT_SENT
            if (orgData.status === organizationStatus.INCOMPLETE) {
              updatePayload.status = organizationStatus.INVITE_NOT_SENT
            }

            // Add update to batch
            orgBatch.update(
              FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc(organizationId),
              updatePayload
            )
          }
        }

        // Commit all organization updates
        await orgBatch.commit()
      }
    }

    // 5. Return counts of new and existing reps
    const result = { added: newReps.length, existing: existingReps.length }

    // Show toast with results
    // if (newReps.length > 0) {
    //   toast.success(`Successfully added ${newReps.length} new representatives.`)
    // }
    // if (existingReps.length > 0) {
    //   toast.info(`Skipped ${existingReps.length} representatives with existing emails.`)
    // }

    return result
  } catch (error) {
    console.error('Error in bulkCreateRepresentatives:', error)
    toast.error('Could not create representatives.')
    return { added: 0, existing: 0 }
  }
}

export const bulkInsertOrganizations = async (organizations, conferenceId) => {
  try {
    const existingOrganizationNames = []

    // Check for existing organizations with the same name in the current conference
    const existingDocsSnapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('conferenceId', '==', conferenceId)
      .get()

    existingDocsSnapshot.docs.forEach((doc) => {
      const docData = doc.data()
      docData?.organization && existingOrganizationNames.push(docData?.organization.toLowerCase())
    })

    // Filter out organizations with names that already exist
    const uniqueOrganizations = organizations.filter(
      (org) => !existingOrganizationNames.includes(org?.organization.toLowerCase())
    )

    const uniqueCount = uniqueOrganizations?.length
    const existingCount = organizations?.length - uniqueCount

    if (uniqueOrganizations.length === 0) {
      return { uniqueCount, existingCount }
    }

    // Firebase batched writes (max 500 per batch)
    const batchSize = 500
    const batchedOrganizationsData = chunk(uniqueOrganizations, batchSize)
    const batch = FirestoreDatabase.batch()

    const batchedPromises = batchedOrganizationsData.map((batchOrgs) => {
      batchOrgs.forEach((org) => {
        const orgCopy = { ...org }
        orgCopy?.id && delete orgCopy['id']
        const docRef = FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc()
        batch.set(docRef, orgCopy)
      })
      return batch.commit()
    })

    await Promise.all(batchedPromises)

    return { uniqueCount, existingCount }
  } catch (err) {
    console.error(err)
    toast.error('Organization import failed. Try again.')
    return { uniqueCount: 0, existingCount: 0 }
  }
}

export default organizationSlice
